import styled from 'styled-components';
import tinycolor from 'tinycolor2';
import { KioskProLogo } from 'src/components/kiosk-pro/kiosk-pro-shared/kiosk-pro-logo';
import { Search } from 'src/dispensary/core-menu/components/header/search';
import { KIOSK_PRO_MEDIA } from 'src/components/kiosk-pro/kiosk-pro-shared/kiosk-pro-shared.styles';

export const KioskProNavContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: inherit;
  height: 100vh;
  padding: 0 24px;
  background: inherit;
  color: ${({ theme }) => theme.colors.grey[30]};
  z-index: 30;

  @media ${KIOSK_PRO_MEDIA.maxWidth} {
    padding: 0 20px;
  }

  @media (orientation: portrait) {
    position: fixed;
    height: auto;
    padding: 24px 36px;
  }

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    padding: 20px 28px;
  }
`;

export const LogoSearchContainer = styled.div`
  display: grid;
  align-items: center;
  grid-template-columns: 1fr 1fr;
  margin: 16px 0 36px;

  @media (orientation: portrait) {
    grid-template-columns: 100px auto 1fr;
    margin: 0;
  }
`;

export const StyledKioskProLogo = styled(KioskProLogo)`
  max-width: 80px;
  padding: 0;
  transition: opacity 300ms ease-in-out;

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    max-width: 60px;
  }

  @media (orientation: portrait) {
    padding: 0;
  }
`;

export const SearchContainer = styled.div`
  grid-column: 1 / span 2;
  width: 100%;
  margin: 52px 0 16px;
  padding: 0;

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    margin-top: 48px;
  }

  @media (orientation: portrait) {
    grid-column: unset;
    margin: 0;
    position: relative;
    z-index: 10;
  }
`;

export const StyledSearch = styled(Search)`
  font-size: 24px;
  color: #485255;
  font-weight: 600;

  input::placeholder {
    color: #485255;
    font-weight: 600;
  }

  svg {
    left: 20px;
    width: 24px;
    height: 24px;
  }

  input[type='text'] {
    padding: 35px 30px 35px 56px;
    font-weight: 600;
    color: #485255;
  }

  @media (orientation: portrait) {
    & > div {
      right: 0;
      left: 0;
      width: calc(100% - 140px);
      margin-left: 140px;
      transition: width 300ms ease-in-out, margin-left 300ms ease-in-out;
    }

    &:focus-within > div {
      width: calc(100% - 20px);
      margin-left: 20px;
    }
  }

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    font-size: 20px;

    svg {
      width: 20px;
      height: 20px;
    }

    & > div {
      width: calc(100% - 70px);
      margin-left: 70px;
      transition: width 300ms ease-in-out, margin-left 300ms ease-in-out;
    }

    input[type='text'] {
      padding: 31px 30px 31px 56px;
    }
  }

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    font-size: 20px;

    input[type='text'] {
      padding: 31px 30px 31px 56px;
    }
  }
`;

export const MenuTypeContainer = styled.div`
  width: 120px;
  justify-self: start;
  color: ${({ theme }) => theme.colors.black};
  font-size: 18px;

  > div > div {
    font-size: 16px;
  }

  button {
    color: ${({ theme }) => theme.colors.black};
    font-size: 16px;
  }

  [class^='menu-type__SingleMenuType'] {
    color: ${({ theme }) => theme.colors.black};
  }

  @media (orientation: landscape) {
    justify-self: end;
  }
`;

export const NavMenuContainer = styled.nav`
  grid-column: 1 / span 2;
  grid-row: 2;
  display: flex;
  flex-direction: column;
  gap: 20px;

  li {
    list-style: none;
  }

  @media (orientation: portrait) {
    flex-direction: row;
    justify-content: flex-start;
    padding: 0;
    gap: 40px;
  }

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    gap: 2px;
  }
`;

export const NavLink = styled.a`
  display: flex;
  align-items: center;
  gap: 10px;
  position: relative;
  color: #485255;
  font-weight: 600;
  font-size: 24px;
  cursor: pointer;
  padding: 16px 0;
  min-height: 44px;
  min-width: 44px;
  -webkit-tap-highlight-color: transparent;
  touch-action: manipulation;
  user-select: none;

  & > svg {
    width: 24px;
    height: 24px;
  }

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    font-size: 20px;
    min-height: 40px;
    min-width: 40px;
  }

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    font-size: 20px;
    min-height: 44px;
    min-width: 44px;
    padding: 12px 0;

    & > svg {
      width: 20px;
      height: 20px;
    }
  }
`;

export const CategoriesNavLink = styled(NavLink)`
  @media (orientation: portrait) {
    order: 5;
    padding-right: 30px;
    margin-left: auto;
  }
`;

export const CategoriesChevron = styled.div`
  position: absolute;
  right: 0;
  display: flex;
  align-items: center;

  svg {
    transform: rotate(270deg);
  }

  @media (orientation: portrait) {
    right: 0;

    svg {
      transform: rotate(0deg);
    }
  }
`;

export const CategoriesAndCollectionsContainer = styled.div`
  position: fixed;
  top: 0;
  left: 400px;
  width: 380px;
  height: 100vh;
  padding: 20px;
  background: ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#fff', 90).toString()};
  overflow-y: auto;
  overflow-x: auto;
  z-index: 25;

  @media ${KIOSK_PRO_MEDIA.maxWidth} {
    left: 320px;
  }

  @media (orientation: portrait) {
    top: 240px;
    left: 0;
    right: 0;
    width: 100%;
    height: auto;
    padding: 0 24px;
  }

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    top: 200px;
  }
`;

export const GradientBottom = styled.div<{ isVisible: boolean }>`
  display: block;
  transform: translateZ(0);
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  pointer-events: none;
  position: fixed;
  bottom: 0;
  left: 400px;
  width: 380px;
  height: 100px;
  z-index: 10;
  will-change: opacity;
  transition: opacity 0.1s;
  background: linear-gradient(
    to bottom,
    ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#fff', 90).setAlpha(0).toRgbString()},
    ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#fff', 90).setAlpha(1).toRgbString()} 100%
  );

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    left: 320px;
  }
`;

export const GridLayout = styled.div`
  display: block;

  @media screen and (orientation: portrait) {
    display: none;
  }
`;

export const CarouselLayout = styled.div`
  display: none;

  @media screen and (orientation: portrait) {
    display: block;
  }

  [class*='carousel-v2__GradientLeft'] {
    background: linear-gradient(
      to left,
      ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#fff', 90).setAlpha(0).toRgbString()},
      ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#fff', 90).setAlpha(1).toRgbString()} 100%
    ) !important;
  }

  [class*='carousel-v2__GradientRight'] {
    background: linear-gradient(
      to right,
      ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#fff', 90).setAlpha(0).toRgbString()},
      ${({ theme }) => tinycolor.mix(theme.customized.colors.buttonsLinks, '#fff', 90).setAlpha(1).toRgbString()} 100%
    ) !important;
  }
`;

export const Divider = styled.div`
  border-top: 2px solid ${({ theme }) => theme.colors.grey[90]};
  margin: 24px 0;

  @media ${KIOSK_PRO_MEDIA.portraitMaxWidth} {
    margin: 20px 0;
  }

  @media ${KIOSK_PRO_MEDIA.landscapeMaxWidth} {
    margin: 20px 0;
  }
`;

export const CategoriesOverlay = styled.div`
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 20;
`;
